/**
 * GPS capture (D-09, `TRACK-D` "GPS must not block the punch").
 *
 * The one rule: this never refuses a punch and never waits longer than the
 * timeout. Policy is `warn` — a worker in a basement, behind a denied
 * permission, or on the plain-HTTP dev build still gets to start their shift;
 * the server records `gps_status` and raises `GPS_DENIED` for review.
 *
 * The result is `gps` XOR `gpsStatus`, which is exactly what
 * `PunchInRequestSchema` / `PunchOutRequestSchema` refine on: coordinates, or
 * the documented reason there are none — never neither, never both.
 */
import { GpsSchema, type Gps } from '@saniplus/contracts';

import type { MissingGpsStatus } from './db';

export const GPS_TIMEOUT_MS = 8_000;

export type GpsCapture = { readonly gps: Gps } | { readonly gpsStatus: MissingGpsStatus };

export interface CaptureGpsOptions {
  readonly timeoutMs?: number;
}

/** `PERMISSION_DENIED` / `POSITION_UNAVAILABLE` / `TIMEOUT` as the spec names them. */
function statusOf(error: GeolocationPositionError): MissingGpsStatus {
  if (error.code === 1) return 'denied';
  if (error.code === 3) return 'timeout';
  return 'unavailable';
}

/**
 * `unavailable` covers both "this browser has no geolocation" and "this page
 * is not a secure context". Local dev runs on plain HTTP by design (HTTPS is
 * production-only), where `navigator.geolocation` exists but every
 * call errors or hangs — reporting it up front keeps the punch instant.
 */
function geolocationOf(): Geolocation | null {
  if (typeof navigator === 'undefined') return null;
  if (typeof isSecureContext === 'boolean' && !isSecureContext) return null;
  // Typed non-optional by the DOM lib, genuinely absent in some WebViews.
  const candidate = (navigator as { geolocation?: Geolocation }).geolocation;
  return candidate ?? null;
}

export function captureGps(options: CaptureGpsOptions = {}): Promise<GpsCapture> {
  const timeoutMs = options.timeoutMs ?? GPS_TIMEOUT_MS;
  const geolocation = geolocationOf();
  if (geolocation === null) return Promise.resolve({ gpsStatus: 'unavailable' });

  return new Promise<GpsCapture>((resolve) => {
    let settled = false;
    const finish = (capture: GpsCapture): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(capture);
    };

    // Our own timer, not only the option: a browser that ignores `timeout`
    // (or a permission prompt the worker never answers) must not leave the
    // punch button spinning.
    const timer = setTimeout(() => {
      finish({ gpsStatus: 'timeout' });
    }, timeoutMs);

    geolocation.getCurrentPosition(
      (position) => {
        const parsed = GpsSchema.safeParse({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM: position.coords.accuracy,
        });
        // Coordinates the frozen schema refuses are not coordinates; sending
        // them would fail validation and lose the punch to a 400.
        finish(parsed.success ? { gps: parsed.data } : { gpsStatus: 'unavailable' });
      },
      (error: GeolocationPositionError) => {
        finish({ gpsStatus: statusOf(error) });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
